// Asset memory-budget heuristics (roadmap D-6). UEFN islands ship under a
// hard runtime memory budget, and over-sized textures / dense meshes are
// the usual culprits. These thresholds are deliberately conservative
// defaults — flags, not gates — meant to draw a reviewer's eye to assets
// worth a second look, not to block anything.
//
// Texture verdict is keyed on the authored source resolution (max
// dimension). UEFN art guidance leans on ≤2048 for most in-world
// textures; 4096 is heavy; beyond that is excessive for all but hero
// surfaces. Resolution is the honest signal we have — the cooked GPU
// format (BC7 etc.) isn't in the source .uasset, so we don't pretend to
// know exact VRAM.
//
// Mesh verdict is triangle count from the glb stats.

export type BudgetVerdict = 'ok' | 'warn' | 'over';

// Texture: max(width, height) in texels.
export const TEXTURE_WARN_DIM = 2048;
export const TEXTURE_OVER_DIM = 4096;

// Mesh: triangle count.
export const MESH_WARN_TRIS = 250_000;
export const MESH_OVER_TRIS = 1_000_000;

export function textureVerdict(maxDim: number): BudgetVerdict {
  if (maxDim > TEXTURE_OVER_DIM) return 'over';
  if (maxDim > TEXTURE_WARN_DIM) return 'warn';
  return 'ok';
}

export function meshVerdict(triangles: number): BudgetVerdict {
  if (triangles > MESH_OVER_TRIS) return 'over';
  if (triangles > MESH_WARN_TRIS) return 'warn';
  return 'ok';
}

export function verdictTone(v: BudgetVerdict): 'green' | 'yellow' | 'red' {
  return v === 'over' ? 'red' : v === 'warn' ? 'yellow' : 'green';
}

export function verdictLabel(v: BudgetVerdict): string {
  return v === 'over' ? 'over budget' : v === 'warn' ? 'large' : 'within budget';
}

// True for asset classes we evaluate as textures (Texture2D,
// TextureCube, Texture2DArray, …).
export function isTextureClass(assetClass: string | null | undefined): boolean {
  return typeof assetClass === 'string' && assetClass.startsWith('Texture');
}

export function isMeshClass(assetClass: string | null | undefined): boolean {
  return assetClass === 'StaticMesh' || assetClass === 'SkeletalMesh';
}

export interface TextureSource {
  sizeX: number;
  sizeY: number;
  format?: string | undefined;
}

// Pull authored texture dimensions out of a UTexture2D properties dump
// (the `[export].Properties.Source` block). Shared by the budget badge
// and the diff strip so the extraction logic lives in one place.
export function textureSourceOf(doc: unknown): TextureSource | null {
  const exports = Array.isArray(doc) ? doc : null;
  if (!exports) return null;
  for (const e of exports) {
    if (typeof e !== 'object' || e === null) continue;
    const props = (e as Record<string, unknown>).Properties;
    if (typeof props !== 'object' || props === null) continue;
    const src = (props as Record<string, unknown>).Source;
    if (typeof src !== 'object' || src === null) continue;
    const s = src as Record<string, unknown>;
    if (typeof s.SizeX !== 'number' || typeof s.SizeY !== 'number') continue;
    return {
      sizeX: s.SizeX,
      sizeY: s.SizeY,
      format: typeof s.Format === 'string' ? s.Format : undefined,
    };
  }
  return null;
}
